import React, { useState, useEffect } from 'react';
import { getTrainingPlan, regenerateTrainingPlan } from '../services/api';

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export default function TrainingPlan({ shareableId }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getTrainingPlan(shareableId)
      .then(setPlan)
      .catch(err => setError(err.response?.data?.error || 'Failed to load training plan'))
      .finally(() => setLoading(false));
  }, [shareableId]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const newPlan = await regenerateTrainingPlan(shareableId);
      setPlan(newPlan);
    } catch {
      // silent
    }
    setRegenerating(false);
  };

  if (loading) return <div style={{ color: '#555', fontSize: 13, padding: 20 }}>Generating your training plan...</div>;
  if (error) return <div style={{ color: '#ef4444', fontSize: 13, padding: 20 }}>{error}</div>;
  if (!plan) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Summary */}
      <p style={{ margin: 0, fontSize: 14, color: '#ccc', lineHeight: 1.7 }}>{plan.summary}</p>

      {/* Weak Dimensions */}
      {plan.weakDimensions?.map((dim, i) => (
        <div key={i} style={{
          background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.15)',
          borderRadius: 10, padding: 16,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#f59e0b' }}>{dim.dimension}</span>
            <span style={{ fontSize: 12, color: '#666' }}>{dim.score}/100</span>
          </div>
          <p style={{ margin: '0 0 12px', fontSize: 12, color: '#888' }}>{dim.diagnosis}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {dim.drills?.map((drill, j) => (
              <div key={j} style={{
                background: 'rgba(0,0,0,0.3)', borderRadius: 8, padding: '10px 14px',
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 4 }}>{drill.name}</div>
                <div style={{ fontSize: 12, color: '#aaa', lineHeight: 1.6 }}>{drill.description}</div>
                <div style={{ fontSize: 11, color: '#666', marginTop: 6 }}>{drill.duration} · {drill.frequency}</div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Weekly Plan */}
      {plan.weeklyPlan && (
        <div>
          <div style={{ fontSize: 11, color: '#555', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 }}>
            7-Day Schedule
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
            {DAY_ORDER.map(day => {
              const tasks = plan.weeklyPlan[day] || [];
              const isRest = tasks.length === 1 && tasks[0].toLowerCase().includes('rest');
              return (
                <div key={day} style={{
                  background: isRest ? 'rgba(255,255,255,0.02)' : 'rgba(245,158,11,0.05)',
                  border: `1px solid ${isRest ? '#1a1a1a' : 'rgba(245,158,11,0.12)'}`,
                  borderRadius: 8, padding: '10px 12px',
                }}>
                  <div style={{ fontSize: 10, color: '#666', fontWeight: 700, textTransform: 'capitalize', marginBottom: 6 }}>{day}</div>
                  {tasks.map((task, i) => (
                    <div key={i} style={{ fontSize: 11, color: isRest ? '#444' : '#ccc', lineHeight: 1.5 }}>{task}</div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tips */}
      {plan.tips?.length > 0 && (
        <div style={{ borderLeft: '2px solid #f59e0b33', paddingLeft: 14 }}>
          {plan.tips.map((tip, i) => (
            <p key={i} style={{ margin: '0 0 6px', fontSize: 12, color: '#888' }}>{tip}</p>
          ))}
        </div>
      )}

      {/* Regenerate */}
      <button
        onClick={handleRegenerate}
        disabled={regenerating}
        style={{
          alignSelf: 'flex-start', padding: '8px 16px', borderRadius: 8,
          fontSize: 12, fontWeight: 600, cursor: 'pointer',
          background: 'transparent', border: '1px solid #333', color: '#666',
          opacity: regenerating ? 0.5 : 1,
        }}
      >
        {regenerating ? 'Regenerating...' : 'Regenerate Plan'}
      </button>
    </div>
  );
}
